import { ChevronDown, ChevronUp, Edit, MoreHorizontal, XCircle } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';

import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Text,
  Tooltip,
} from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { EventTriggerType, EXPONENT_VENUE_LABELS, ExponentVenue } from '../../domain';
import { PendingTrigger } from '../../stores/pendingTriggersStore';

interface Props {
  trigger: PendingTrigger;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  error?: string;
}

interface FieldItem {
  label: string;
  value: string;
}

const MAX_VISIBLE_CHIPS = 4;

const maskAddress = (address: string): string => {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const formatPercentage = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return `${num}%`;
};

export const PendingTriggerItem = ({ trigger, onEdit, onDelete, error }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getChainName } = useGetChainInfo();
  const formValues = trigger.formValues;

  const handleEdit = () => {
    onEdit(trigger.id);
  };

  const handleDelete = () => {
    onDelete(trigger.id);
  };

  const getFields = (): FieldItem[] => {
    const fields: FieldItem[] = [];
    const isOnChainTrigger =
      trigger.triggerType === EventTriggerType.OnChain || trigger.triggerType === EventTriggerType.OnChainFunction;
    const isOffChainTrigger = trigger.triggerType === EventTriggerType.CustomEvent;

    // Network
    if (formValues.chainId && typeof formValues.chainId === 'number') {
      const chainName = getChainName(formValues.chainId);
      fields.push({ label: 'Network', value: chainName || `Chain ${formValues.chainId}` });
    }

    // Contract chainId (onchain triggers)
    const contract = formValues.contract as { contractAddress?: string; chainId?: number } | undefined;
    if (contract?.chainId && typeof contract.chainId === 'number' && !formValues.chainId) {
      const chainName = getChainName(contract.chainId);
      fields.push({ label: 'Network', value: chainName || `Chain ${contract.chainId}` });
    }

    // Contract address (for onchain triggers)
    if (contract?.contractAddress && typeof contract.contractAddress === 'string') {
      fields.push({ label: 'Contract', value: maskAddress(contract.contractAddress) });
    }

    // OnChain triggers
    if (isOnChainTrigger) {
      if (formValues.name && typeof formValues.name === 'string') {
        fields.push({ label: 'Name', value: formValues.name });
      }
      if (formValues.type && typeof formValues.type === 'string') {
        const typeLabel = formValues.type === 'on-chain-event' ? 'Event' : 'Function';
        fields.push({ label: 'Type', value: typeLabel });
      }
      if (formValues.signature && typeof formValues.signature === 'string') {
        const signatureName = formValues.signature.split('(')[0];
        fields.push({ label: 'Signature', value: signatureName });
      }
      return fields;
    }

    // OffChain triggers (Custom Event)
    if (isOffChainTrigger) {
      if (formValues.name && typeof formValues.name === 'string') {
        fields.push({ label: 'Name', value: formValues.name });
      }
      if (formValues.signature && typeof formValues.signature === 'string') {
        fields.push({ label: 'Event name', value: formValues.signature });
      }
      return fields;
    }

    // Token address
    if (formValues.tokenAddress && typeof formValues.tokenAddress === 'string') {
      fields.push({ label: 'Token', value: maskAddress(formValues.tokenAddress) });
    }

    // Pool address
    if (formValues.poolAddress && typeof formValues.poolAddress === 'string') {
      fields.push({ label: 'Pool', value: maskAddress(formValues.poolAddress) });
    }

    // Market address
    if (formValues.marketAddress && typeof formValues.marketAddress === 'string') {
      fields.push({ label: 'Market', value: maskAddress(formValues.marketAddress) });
    }

    // Contract address for non-onchain triggers
    if (formValues.contractAddress && typeof formValues.contractAddress === 'string') {
      fields.push({ label: 'Contract', value: maskAddress(formValues.contractAddress) });
    }

    // Token currency
    if (formValues.tokenCurrency && typeof formValues.tokenCurrency === 'string') {
      fields.push({ label: 'Currency', value: formValues.tokenCurrency });
    }

    // Strategy
    if (formValues.strategy && typeof formValues.strategy === 'string') {
      const strategyLabels: Record<string, string> = {
        ActiveLiquidity: 'Active Liquidity',
        DistributionFormula: 'Distribution Formula',
        Lend: 'Lend',
        Borrow: 'Borrow',
        Supply: 'Supply',
      };
      fields.push({ label: 'Strategy', value: strategyLabels[formValues.strategy] || formValues.strategy });
    }

    // Position type
    if (formValues.positionType && typeof formValues.positionType === 'string') {
      fields.push({ label: 'Position type', value: formValues.positionType });
    }

    // Volume type
    if (formValues.volumeType && typeof formValues.volumeType === 'string') {
      fields.push({ label: 'Volume type', value: formValues.volumeType });
    }

    // Referred fees level
    if (formValues.referredFeesLevel && typeof formValues.referredFeesLevel === 'string') {
      fields.push({ label: 'Referred fees level', value: formValues.referredFeesLevel });
    }

    // Asset and market (Pyron)
    if (formValues.asset && typeof formValues.asset === 'string') {
      fields.push({ label: 'Asset', value: formValues.asset });
    }
    if (formValues.market && typeof formValues.market === 'string') {
      fields.push({ label: 'Market', value: formValues.market });
    }

    // Vault and venue (Exponent). Two Exponent triggers on the same vault differ only by venue,
    // so without these the pending cards are indistinguishable.
    if (formValues.vaultAddress && typeof formValues.vaultAddress === 'string') {
      fields.push({ label: 'Vault', value: maskAddress(formValues.vaultAddress) });
    }
    if (formValues.venue && typeof formValues.venue === 'string') {
      fields.push({
        label: 'Venue',
        value: EXPONENT_VENUE_LABELS[formValues.venue as ExponentVenue] ?? formValues.venue,
      });
    }

    // Percentage fields
    if (
      formValues.token_0 !== undefined &&
      (typeof formValues.token_0 === 'number' || typeof formValues.token_0 === 'string')
    ) {
      fields.push({ label: 'Token 0', value: formatPercentage(formValues.token_0) });
    }
    if (
      formValues.token_1 !== undefined &&
      (typeof formValues.token_1 === 'number' || typeof formValues.token_1 === 'string')
    ) {
      fields.push({ label: 'Token 1', value: formatPercentage(formValues.token_1) });
    }
    if (
      formValues.liquidity !== undefined &&
      (typeof formValues.liquidity === 'number' || typeof formValues.liquidity === 'string')
    ) {
      fields.push({ label: 'Liquidity', value: formatPercentage(formValues.liquidity) });
    }

    // Username (X/Twitter)
    if (formValues.usernameToFollow && typeof formValues.usernameToFollow === 'string') {
      fields.push({ label: 'Username', value: `@${formValues.usernameToFollow}` });
    }

    // Post text
    if (formValues.postText && typeof formValues.postText === 'string') {
      const truncatedText =
        formValues.postText.length > 50 ? `${formValues.postText.slice(0, 50)}...` : formValues.postText;
      fields.push({ label: 'Post text', value: truncatedText });
    }

    // Post URL (X repost)
    if (formValues.postUrl && typeof formValues.postUrl === 'string') {
      fields.push({ label: 'Post URL', value: formValues.postUrl });
    }

    // Quest URL
    if (formValues.questUrl && typeof formValues.questUrl === 'string') {
      fields.push({ label: 'Quest URL', value: formValues.questUrl });
    }

    // Query URL
    if (formValues.queryUrl && typeof formValues.queryUrl === 'string') {
      fields.push({ label: 'Query URL', value: formValues.queryUrl });
    }

    // Subgraph name
    if (formValues.subgraphName && typeof formValues.subgraphName === 'string') {
      fields.push({ label: 'Subgraph', value: formValues.subgraphName });
    }

    // Polymarket market
    if (formValues.conditionId && typeof formValues.conditionId === 'string') {
      if (formValues.name && typeof formValues.name === 'string') {
        fields.push({ label: 'Market', value: formValues.name });
      }
      fields.push({ label: 'Condition ID', value: maskAddress(formValues.conditionId) });
    }

    // Name (generic fallback)
    if (formValues.name && typeof formValues.name === 'string' && fields.length === 0) {
      fields.push({ label: 'Name', value: formValues.name });
    }

    return fields;
  };

  const fields = getFields();
  const visibleFields = fields.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenFields = fields.slice(MAX_VISIBLE_CHIPS);
  const hasMoreFields = hiddenFields.length > 0;

  return (
    <div className={`tw:p-4 tw:rounded tw:border ${error ? 'tw:border-red-500 tw:bg-red-50' : 'tw:border-border'}`}>
      <div className="tw:flex tw:flex-row tw:items-start tw:gap-4">
        {typeof trigger.triggerOption.imageSrc === 'string' ? (
          <Image alt={trigger.triggerOption.title} width={32} height={32} src={trigger.triggerOption.imageSrc} />
        ) : (
          <div className="tw:w-8 tw:h-8 tw:flex tw:justify-center tw:items-center">
            {trigger.triggerOption.imageSrc}
          </div>
        )}

        <div className="tw:flex tw:flex-col tw:flex-1 tw:gap-2">
          <Text variant="body-sm" weight="bold">
            {trigger.triggerOption.title}
          </Text>
          <Text variant="body-sm" color="muted">
            {trigger.triggerOption.description}
          </Text>

          {error && (
            <Text variant="body-sm" className="tw:text-red-500">
              {error}
            </Text>
          )}

          {visibleFields.length > 0 && (
            <div className="tw:flex tw:flex-row tw:flex-wrap tw:gap-1">
              {visibleFields.map((field, index) => (
                <Badge key={index} size="sm" variant="outline">{`${field.label}: ${field.value}`}</Badge>
              ))}
            </div>
          )}

          {hasMoreFields && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsExpanded(!isExpanded)}
                className="tw:self-start tw:p-0 tw:min-w-0"
              >
                {isExpanded ? 'Hide fields' : 'Show more fields'}
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>

              <Collapsible open={isExpanded}>
                <CollapsibleContent>
                  <div className="tw:mt-2 tw:p-3 tw:rounded tw:bg-accent">
                    <div className="tw:flex tw:flex-col tw:gap-1">
                      {hiddenFields.map((field, index) => (
                        <div key={index} className="tw:flex tw:flex-row tw:gap-2">
                          <Text variant="body-sm" color="muted" className="tw:min-w-[120px]">
                            {field.label}:
                          </Text>
                          <Text variant="body-sm" className="tw:break-all">
                            {field.value}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
        </div>

        <DropdownMenu>
          <Tooltip content="More options">
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
          </Tooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit}>
              <Edit size={16} />
              <Text variant="body-sm">Edit</Text>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDelete}>
              <XCircle size={16} />
              <Text variant="body-sm">Delete</Text>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
